///
/// EarthBrowser
///
/// Created by Matt Giger on September 13, 2026
/// Copyright (c) 2026 Geodata Labs
///

/// Storage Manager overview: quota usage, storage category breakdown, and the
/// factory-reset danger zone (customer support last resort).

import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { requestFactoryReset } from '@/utils/factoryReset'
import { AlertTriangle, Bomb, Database, FileArchive, HardDrive, KeyRound } from 'lucide-react'
import { useState } from 'react'
import type { StorageTabProps } from '@/components/features/dev-tools/storage-manager/StorageManager'
import { formatBytes } from '@/components/features/dev-tools/storage-manager/storageScanner'

const RESET_CONFIRM_WORD = 'RESET'

export function OverviewTab({ snapshot }: StorageTabProps) {
	const [resetDialogOpen, setResetDialogOpen] = useState(false)
	const [resetConfirmText, setResetConfirmText] = useState('')

	const usagePercent = snapshot.quota > 0 ? (snapshot.usage / snapshot.quota) * 100 : 0
	const opfsPercent = snapshot.usage > 0 ? (snapshot.opfsTotal / snapshot.usage) * 100 : 0
	const localStorageTotal = snapshot.localStorageItems.reduce((sum, item) => sum + item.size, 0)
	const vfsTotal = snapshot.volumes.reduce((sum, v) => sum + v.totalSize, 0)
	const gpkgTotal = snapshot.geopackages.reduce((sum, f) => sum + f.size, 0)

	const categories = [
		{
			icon: <HardDrive className="h-4 w-4 text-blue-400" />,
			label: 'Database Volumes (VFS)',
			detail: `${snapshot.volumes.length} volumes • ${snapshot.volumes.reduce((sum, v) => sum + v.files.length, 0)} files`,
			size: vfsTotal,
		},
		{
			icon: <FileArchive className="h-4 w-4 text-teal-400" />,
			label: 'GeoPackages',
			detail: `${snapshot.geopackages.length} .gpkg files`,
			size: gpkgTotal,
		},
		{
			icon: <Database className="h-4 w-4 text-cyan-400" />,
			label: 'IndexedDB',
			detail: `${snapshot.indexedDBs.length} databases`,
			size: snapshot.indexedDBEstimate,
			estimated: true,
		},
		{
			icon: <KeyRound className="h-4 w-4 text-amber-400" />,
			label: 'Local Storage',
			detail: `${snapshot.localStorageItems.length} keys`,
			size: localStorageTotal,
		},
	]

	const confirmReset = () => {
		setResetDialogOpen(false)
		requestFactoryReset()
	}

	return (
		<div className="space-y-4">
			<Card>
				<CardHeader>
					<CardTitle className="text-base">Browser Storage Quota</CardTitle>
					<CardDescription>
						{formatBytes(snapshot.usage)} used of {formatBytes(snapshot.quota)} available ({usagePercent.toFixed(2)}%)
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-3">
					{/* Usage bar with OPFS vs IndexedDB segmentation */}
					<div className="space-y-1">
						<div className="w-full bg-secondary rounded-full h-3 overflow-hidden flex">
							<div
								className="bg-blue-500 h-3"
								style={{ width: `${usagePercent * (opfsPercent / 100)}%` }}
								title={`Files (OPFS): ${formatBytes(snapshot.opfsTotal)}`}
							/>
							<div
								className="bg-cyan-500 h-3"
								style={{ width: `${usagePercent * (1 - opfsPercent / 100)}%` }}
								title={`IndexedDB (estimated): ${formatBytes(snapshot.indexedDBEstimate)}`}
							/>
						</div>
						<div className="flex items-center gap-4 text-xs text-muted-foreground">
							<span className="flex items-center gap-1.5">
								<span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
								Files (OPFS): {formatBytes(snapshot.opfsTotal)}
							</span>
							<span className="flex items-center gap-1.5">
								<span className="inline-block w-2 h-2 rounded-full bg-cyan-500" />
								IndexedDB (est.): {formatBytes(snapshot.indexedDBEstimate)}
							</span>
						</div>
					</div>

					<div className="flex items-center gap-2">
						<Badge variant={snapshot.isPersisted ? 'default' : 'outline'} className="text-xs">
							{snapshot.isPersisted ? '✓ Persisted' : '⚠ Not Persisted'}
						</Badge>
						{!snapshot.isPersisted && (
							<span className="text-xs text-yellow-500">
								Data may be evicted by the browser under storage pressure
							</span>
						)}
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle className="text-base">What&apos;s Stored</CardTitle>
					<CardDescription>Use the tabs above to inspect and manage each category</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="space-y-1">
						{categories.map(category => (
							<div key={category.label} className="flex items-center justify-between p-2 bg-secondary/30 rounded">
								<div className="flex items-center gap-2">
									{category.icon}
									<div>
										<div className="text-sm font-medium">{category.label}</div>
										<div className="text-xs text-muted-foreground">{category.detail}</div>
									</div>
								</div>
								<span className="font-mono text-xs">
									{category.estimated && '~'}{formatBytes(category.size)}
								</span>
							</div>
						))}
					</div>
				</CardContent>
			</Card>

			<Card className="border-2 border-red-900/70">
				<CardHeader>
					<CardTitle className="text-base flex items-center gap-2 text-red-400">
						<AlertTriangle className="h-4 w-4" />
						Danger Zone
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-3">
					<div className="flex items-start justify-between gap-4">
						<div className="space-y-1 text-sm">
							<div className="font-medium">Factory Reset</div>
							<div className="text-xs text-muted-foreground">
								Erases everything EarthBrowser stores in this browser: databases, imported
								files, settings, caches, and cookies. Server account data is not affected.
								The wipe runs on reload, before database locks are taken, so it works even
								when volumes are locked. Use as a last resort to recover from corrupted
								local state.
							</div>
							<div className="text-xs text-muted-foreground">
								Support tip: customers can trigger this without opening this window by
								visiting the app with <code className="font-mono bg-secondary/50 px-1 rounded">?factory-reset</code>{' '}
								added to the URL.
							</div>
						</div>
						<AlertDialog open={resetDialogOpen} onOpenChange={(open) => {
							setResetDialogOpen(open)
							if (!open) setResetConfirmText('')
						}}>
							<AlertDialogTrigger asChild>
								<Button variant="destructive" size="sm">
									<Bomb className="h-4 w-4 mr-2" />
									Factory Reset
								</Button>
							</AlertDialogTrigger>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>Factory reset EarthBrowser?</AlertDialogTitle>
									<AlertDialogDescription className="space-y-2">
										This permanently deletes all local data — databases, imported GeoPackages,
										saved settings, caches, and cookies — then reloads the app in a
										completely fresh state. This cannot be undone.
									</AlertDialogDescription>
								</AlertDialogHeader>
								<div className="space-y-2">
									<div className="text-sm text-muted-foreground">
										Type <span className="font-mono font-semibold text-foreground">{RESET_CONFIRM_WORD}</span> to confirm:
									</div>
									<Input
										value={resetConfirmText}
										onChange={(e) => setResetConfirmText(e.target.value)}
										placeholder={RESET_CONFIRM_WORD}
										autoFocus
									/>
								</div>
								<AlertDialogFooter>
									<AlertDialogCancel>Cancel</AlertDialogCancel>
									<Button
										variant="destructive"
										disabled={resetConfirmText !== RESET_CONFIRM_WORD}
										onClick={confirmReset}
									>
										Erase Everything &amp; Reload
									</Button>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}
